import { AppUserSession, UserSavedCalculation, UserSettings } from '../../types';

export interface UserOperation {
  id: string;
  title: string;
  type: 'CALCULATION' | 'DATA_SOURCE' | 'SETTINGS' | 'AUTH';
  timestamp: string;
  details: string;
}

class AuthService {
  private currentSession: AppUserSession = {
    id: 'usr-guest',
    email: '',
    fullName: '',
    role: 'USER',
    createdAt: '2026-01-10',
    lastLoginAt: '',
    isLoggedIn: false,
  };

  private savedCalculations: UserSavedCalculation[] = [
    {
      id: 'calc-101',
      userId: 'usr-demo-01',
      title: 'Senior Software Engineer Salary & Tax (France 2026)',
      calculatorType: 'SALARY_TAX',
      inputs: { grossSalary: 75000, country: 'France', regime: 'Cadre' },
      outputs: { netMonthly: 4650, totalTax: 16500 },
      notes: 'Optimized with Navigo & Urssaf 2026 tax schedules',
      createdAt: '2026-08-20',
    },
    {
      id: 'calc-102',
      userId: 'usr-demo-01',
      title: 'USD to EUR Transfer (50,000 USD)',
      calculatorType: 'FX_TRANSFER',
      inputs: { sourceAmount: 50000, sourceCurrency: 'USD', targetCurrency: 'EUR' },
      outputs: { convertedAmount: 46250, midMarketRate: 0.925 },
      notes: 'Corporate treasury liquidity transfer calculation',
      createdAt: '2026-08-25',
    },
  ];

  private recentOperations: UserOperation[] = [
    {
      id: 'op-1',
      title: 'Saved Calculation',
      type: 'CALCULATION',
      timestamp: '2026-09-02 14:15:00',
      details: 'Senior Software Engineer Salary & Tax (France 2026)',
    },
    {
      id: 'op-2',
      title: 'Data Source Sync',
      type: 'DATA_SOURCE',
      timestamp: '2026-09-02 12:00:00',
      details: 'ECB FX Reference Rates refreshed successfully',
    },
    {
      id: 'op-3',
      title: 'Currency Pinned',
      type: 'CALCULATION',
      timestamp: '2026-09-02 10:30:00',
      details: 'Added EUR and GBP to favorite currencies',
    },
    {
      id: 'op-4',
      title: 'Saved Calculation',
      type: 'CALCULATION',
      timestamp: '2026-08-25 16:20:00',
      details: 'USD to EUR Transfer (50,000 USD)',
    },
    {
      id: 'op-5',
      title: 'Session Login',
      type: 'AUTH',
      timestamp: '2026-08-20 09:00:00',
      details: 'Authenticated as Enterprise Admin',
    },
  ];

  private userSettings: UserSettings = {
    userId: 'usr-demo-01',
    defaultCurrency: 'EUR',
    emailNotifications: true,
    twoFactorEnabled: true,
    themeMode: 'LIGHT',
    language: 'fr',
  };

  public getSession(): AppUserSession {
    return this.currentSession;
  }

  public getRecentOperations(): UserOperation[] {
    return this.recentOperations;
  }

  public addRecentOperation(op: Omit<UserOperation, 'id' | 'timestamp'>): void {
    const newOp: UserOperation = {
      ...op,
      id: `op-${Date.now()}`,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
    };
    this.recentOperations.unshift(newOp);
    if (this.recentOperations.length > 5) {
      this.recentOperations.pop();
    }
  }

  public login(email: string, password: string): AppUserSession {
    if (!email || !password) throw new Error('Email and password are required.');
    this.currentSession = {
      id: `usr-${Math.floor(Math.random() * 9000 + 1000)}`,
      email,
      fullName: email.split('@')[0].replace('.', ' ').toUpperCase(),
      role: email.includes('admin') ? 'ENTERPRISE_ADMIN' : 'USER',
      createdAt: new Date().toISOString().split('T')[0],
      lastLoginAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
      isLoggedIn: true,
    };
    this.addRecentOperation({
      title: 'User Login',
      type: 'AUTH',
      details: `Signed in as ${email}`,
    });
    return this.currentSession;
  }

  public signup(fullName: string, email: string, password: string): AppUserSession {
    if (!fullName || !email || !password) throw new Error('All fields are required for sign up.');
    this.currentSession = {
      id: `usr-${Math.floor(Math.random() * 9000 + 1000)}`,
      email,
      fullName,
      role: 'USER',
      createdAt: new Date().toISOString().split('T')[0],
      lastLoginAt: new Date().toISOString().replace('T', ' ').substring(0, 19),
      isLoggedIn: true,
    };
    this.addRecentOperation({
      title: 'Account Created',
      type: 'AUTH',
      details: `New account registered for ${fullName}`,
    });
    return this.currentSession;
  }

  public logout(): void {
    this.currentSession = {
      ...this.currentSession,
      isLoggedIn: false,
    };
  }

  public requestPasswordReset(email: string): boolean {
    if (!email) throw new Error('Email is required for password reset.');
    return true;
  }

  public getSavedCalculations(): UserSavedCalculation[] {
    return this.savedCalculations;
  }

  public saveCalculation(calc: Omit<UserSavedCalculation, 'id' | 'userId' | 'createdAt'>): UserSavedCalculation {
    const newCalc: UserSavedCalculation = {
      ...calc,
      id: `calc-${Date.now()}`,
      userId: this.currentSession.id,
      createdAt: new Date().toISOString().split('T')[0],
    };
    this.savedCalculations.unshift(newCalc);
    this.addRecentOperation({
      title: 'Calculation Saved',
      type: 'CALCULATION',
      details: calc.title,
    });
    return newCalc;
  }

  public deleteSavedCalculation(id: string): void {
    const calc = this.savedCalculations.find((c) => c.id === id);
    this.savedCalculations = this.savedCalculations.filter((c) => c.id !== id);
    if (calc) {
      this.addRecentOperation({
        title: 'Calculation Deleted',
        type: 'CALCULATION',
        details: calc.title,
      });
    }
  }

  public getUserSettings(): UserSettings {
    return this.userSettings;
  }

  public updateUserSettings(settings: Partial<UserSettings>): UserSettings {
    this.userSettings = { ...this.userSettings, ...settings };
    this.addRecentOperation({
      title: 'Settings Updated',
      type: 'SETTINGS',
      details: 'User preference configuration updated',
    });
    return this.userSettings;
  }
}

export const authService = new AuthService();
